import {ApiConverter, DeploymentSpecification, Deployment, TemplateSpecification, RawDeploymentSpecification, DeploymentProposal, DeploymentProposalSpecification} from '@cquential/api';
import cloneDeep from 'lodash/cloneDeep';
import {DeploymentDragData} from '@/types';
import Dates from '@cquential/date-utils';
import {WrDragData} from '@/types/types';

export default class DeploymentUtil {
    public static convertDeploymentDragDataToRosterDeployment(draggedData: WrDragData | DeploymentDragData): Deployment {
        const deployment: Deployment = {
            date: new Date(draggedData.date),
            shiftId: draggedData.shiftId,
            stateId: draggedData.stateId,
            publicComment: null,
            resourceId: null,
            companyId: null,
            companyEmployeeId: null,
        };

        if (DeploymentUtil.draggedDataIsDeploymentDragData(draggedData)) {
            deployment.publicComment = draggedData.publicComment;
            deployment.resourceId = draggedData.resourceId ? draggedData.resourceId : null;
            deployment.companyId = draggedData.companyId ? draggedData.companyId : null;
            deployment.companyEmployeeId = draggedData.companyEmployeeId ? draggedData.companyEmployeeId : null;
            deployment.companyEmployee = draggedData.companyEmployee;

            deployment.specified = draggedData.specified;
            deployment.specifications = draggedData.specifications!.map((specification: RawDeploymentSpecification): DeploymentSpecification => {
                return ApiConverter.convertRawDeploymentSpecificationToSpecification(specification);
            });
        }

        if ('deploymentId' in draggedData) {
            deployment.id = draggedData.deploymentId;
        }

        if ('positionAssignmentPositionDeployments' in draggedData) {
            deployment.positionAssignmentSetGroupPositionDeployments = draggedData.positionAssignmentPositionDeployments;
        }

        return deployment;
    }

    public static convertDeploymentToDeploymentProposal(deployment: Deployment): DeploymentProposal {
        if (deployment.resourceId === null || deployment.companyId !== null) {
            throw new Error('Only deployments for resources can be converted to a DeploymentProposal.');
        }

        const specified: boolean = !!deployment.specified;
        let specifications: DeploymentProposalSpecification[] | undefined;

        if (specified) {
            specifications = deployment.specifications ? deployment.specifications.map(this.convertDeploymentSpecificationToDeploymentProposalSpecification) : [];
        } else {
            specifications = undefined;
        }

        return {
            ...deployment,
            refused: false,
            deploymentId: null,
            specified,
            resourceId: deployment.resourceId!,
            specifications,
        };
    }

    public static convertDeploymentProposalToDeployment(deploymentProposal: DeploymentProposal): Deployment {
        let specifications: DeploymentSpecification[] | undefined;

        if (deploymentProposal) {
            specifications = deploymentProposal.specifications ? deploymentProposal.specifications.map(this.convertDeploymentProposalSpecificationToDeploymentSpecification) : [];
        } else {
            specifications = undefined;
        }

        return {
            publicComment: deploymentProposal.publicComment,
            date: deploymentProposal.date,
            shiftId: deploymentProposal.shiftId,
            stateId: deploymentProposal.stateId,
            resourceId: deploymentProposal.resourceId,
            companyId: null,
            companyEmployeeId: null,
            specified: deploymentProposal.specified,
            specifications,
        };
    }

    public static convertDeploymentSpecificationToDeploymentProposalSpecification(specification: DeploymentSpecification): DeploymentProposalSpecification {
        return {
            ...specification,
            projectId: specification.projectId ?? null,
            activityId: specification.activityId ?? null,
            leaveKindId: specification.leaveKind ?? null,
        };
    }

    public static convertDeploymentProposalSpecificationToDeploymentSpecification(specification: DeploymentProposalSpecification): DeploymentSpecification {
        return {
            ...specification,
            projectId: specification.projectId,
            activityId: specification.activityId,
            leaveKind: specification.leaveKindId,
            costCenterId: null,
        };
    }

    // Properly updates Deployment date and underlying specifications dates
    public static cloneWithNewDate(deployment: Deployment, newDate: Date): Deployment {
        const deploymentClone: Deployment = cloneDeep(deployment);
        const dayDifference: number = Dates.Utils.diffInDays(newDate, deploymentClone.date);

        deploymentClone.date = newDate;

        if (deploymentClone.specifications) {
            deploymentClone.specifications = DeploymentUtil.addDaysToDeploymentSpecifications(deploymentClone.specifications, dayDifference);
        }

        return deploymentClone;
    }

    public static createSpecificationsFromTemplateSpecifications(deploymentDate: Date, specifications: TemplateSpecification[] | undefined, stateId: number): DeploymentSpecification[] | undefined {
        if (!specifications) {
            return undefined;
        }

        return specifications.map((specification: TemplateSpecification): DeploymentSpecification => {
            const beginTime: number = Dates.Time.parse(specification.begin);
            const endTime: number = Dates.Time.parse(specification.end);

            const begin: Date = new Date(deploymentDate);
            begin.setSeconds(begin.getSeconds() + beginTime);

            const end: Date = new Date(deploymentDate);
            end.setSeconds(end.getSeconds() + endTime);

            return {
                begin,
                end,
                stateId,
                typeId: specification.typeId,
                costCenterId: null,
                leaveKind: null,
                projectId: null,
                activityId: null,
            };
        });
    }

    private static addDaysToDeploymentSpecifications(specifications: DeploymentSpecification[], days: number): DeploymentSpecification[] {
        const newSpecifications: DeploymentSpecification[] = [];
        for (const specification of specifications) {
            newSpecifications.push({
                ...specification,
                begin: Dates.Utils.addDays(specification.begin, days),
                end: Dates.Utils.addDays(specification.end, days),
            });
        }

        return newSpecifications;
    }

    private static draggedDataIsDeploymentDragData(draggedData: WrDragData | DeploymentDragData): draggedData is DeploymentDragData {
        return 'specified' in draggedData;
    }

    public static sortOnSpecificationBeginTimesAscending(deployments: Deployment[]): Deployment[] {
        return deployments.sort((a: Deployment, b: Deployment): number => {
            if (!a.specifications || !b.specifications) {
                if (!a.specifications && !b.specifications) {
                    return 0;
                }

                return a.specifications ? 1 : -1; // deployments without specifications will be sorted last
            }

            if (a.specifications!.length === 0 || b.specifications!.length === 0) {
                return a.specifications!.length - b.specifications!.length;
            }

            const aStart: number = a.specifications[0].begin.getTime();
            const bStart: number = b.specifications[0].begin.getTime();

            return aStart - bStart;
        });
    }
}
